import React, { useState, useEffect } from "react"
import { Plus, Pencil, Trash2, Loader2, Zap, ShieldCheck, Users, User } from "lucide-react"
import { Button } from "src/components/ui/button"
import { Input } from "src/components/ui/input"
import { Checkbox } from "src/components/ui/checkbox"
import { Label } from "src/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "src/components/ui/select"
import { useToast } from "src/components/ui/toast"
import { Dialog, DialogContent } from "src/components/ui/dialog"
import { Switch } from "src/components/ui/switch"
import { Can } from "src/components/shared/Can"
import api from "src/apis/api"

const STRATEGIES = [
  { value: "load_balanced", label: "Load Balanced", desc: "Assign to agent with fewest active leads" },
  { value: "round_robin",   label: "Round-Robin",   desc: "Cycle through agents in order" },
  { value: "manual",        label: "Manual Only",   desc: "No auto-assignment; requires manual assign" },
]

async function fetchRules()              { const { data } = await api.get("/api/v1/distribution_rules"); return data.distribution_rules || [] }
async function createRule(rule)          { const { data } = await api.post("/api/v1/distribution_rules", { distribution_rule: rule }); return data.distribution_rule }
async function updateRule(id, rule)      { const { data } = await api.patch(`/api/v1/distribution_rules/${id}`, { distribution_rule: rule }); return data.distribution_rule }
async function deleteRule(id)            { return api.delete(`/api/v1/distribution_rules/${id}`) }

async function fetchMembers()            { const { data } = await api.get("/api/v1/memberships"); return data.memberships || [] }
async function fetchTeams()              { const { data } = await api.get("/api/v1/teams"); return data.teams || [] }

function RuleModal({ open, onClose, onSaved, rule }) {
  const { toast } = useToast()
  const [saving, setSaving] = useState(false)
  const [members, setMembers] = useState([])
  const [teams, setTeams] = useState([])
  const [form, setForm] = useState({ name: "", strategy: "load_balanced", sla_minutes: 60, assignee_pool: [], conditions: {} })

  useEffect(() => {
    fetchMembers().then((ms) => setMembers(ms)).catch(() => {})
    fetchTeams().then((ts) => setTeams(ts)).catch(() => {})
  }, [])

  useEffect(() => {
    if (rule) {
      setForm({
        name:          rule.name || "",
        strategy:      rule.strategy || "load_balanced",
        sla_minutes:   rule.sla_minutes || 60,
        assignee_pool: rule.assignee_pool || [],
        conditions:    rule.conditions || {},
      })
    } else {
      setForm({ name: "", strategy: "load_balanced", sla_minutes: 60, assignee_pool: [], conditions: {} })
    }
  }, [rule, open])

  if (!open) return null

  function toggleMember(userId, roleName) {
    setForm((f) => {
      const pool = f.assignee_pool || []
      const exists = pool.find((e) => e.user_id === userId)
      return {
        ...f,
        assignee_pool: exists
          ? pool.filter((e) => e.user_id !== userId)
          : [...pool, { user_id: userId, role: roleName }],
      }
    })
  }

  function toggleTeam(teamId, teamName) {
    setForm((f) => {
      const pool = f.assignee_pool || []
      const exists = pool.find((e) => e.team_id === teamId)
      return {
        ...f,
        assignee_pool: exists
          ? pool.filter((e) => e.team_id !== teamId)
          : [...pool, { team_id: teamId, name: teamName }],
      }
    })
  }

  async function handleSubmit(e) {
    e.preventDefault()
    setSaving(true)
    try {
      const saved = rule ? await updateRule(rule.id, form) : await createRule(form)
      toast({ title: rule ? "Rule updated" : "Rule created" })
      onSaved(saved)
      onClose()
    } catch (err) {
      toast({ variant: "destructive", title: "Failed", description: err.response?.data?.errors?.[0] })
    } finally {
      setSaving(false)
    }
  }

  const poolUserIds = new Set((form.assignee_pool || []).filter((e) => e.user_id).map((e) => e.user_id))
  const poolTeamIds = new Set((form.assignee_pool || []).filter((e) => e.team_id).map((e) => e.team_id))
  const selectedStrategy = STRATEGIES.find((s) => s.value === form.strategy)

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg">
        <h2 className="text-lg font-bold text-slate-900 mb-5">{rule ? "Edit Rule" : "New Distribution Rule"}</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label>Rule Name</Label>
            <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. Meta Leads → Sales" />
          </div>

          <div className="space-y-1.5">
            <Label>Assignment Strategy</Label>
            <Select value={form.strategy} onValueChange={(v) => setForm((f) => ({ ...f, strategy: v }))}>
              <SelectTrigger className="text-left [&>span]:text-left [&>span]:flex-1">
                <SelectValue placeholder="Select Strategy">
                  {selectedStrategy?.label}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {STRATEGIES.map((s) => (
                  <SelectItem key={s.value} value={s.value}>
                    <div className="text-left">
                      <div className="font-medium">{s.label}</div>
                      <div className="text-[11px] text-slate-500">{s.desc}</div>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label>First-Response SLA (Minutes)</Label>
            <Input
              type="number"
              min={5}
              value={form.sla_minutes}
              onChange={(e) => setForm((f) => ({ ...f, sla_minutes: Number(e.target.value) }))}
            />
          </div>

          {/* Teams */}
          {teams.length > 0 && (
            <div className="space-y-1.5">
              <Label className="flex items-center gap-1.5">
                <Users className="h-3.5 w-3.5 text-slate-400" /> Eligible Teams
              </Label>
              <p className="text-[11px] text-slate-500 -mt-0.5">
                When a team is selected, leads are auto-assigned to its members using the strategy above.
              </p>
              <div className="max-h-32 overflow-y-auto rounded-lg border border-slate-200 divide-y divide-slate-100">
                {teams.map((t) => (
                  <label key={t.id} className="flex cursor-pointer items-center gap-3 px-3 py-2 hover:bg-slate-50">
                    <Checkbox
                      checked={poolTeamIds.has(t.id)}
                      onCheckedChange={() => toggleTeam(t.id, t.name)}
                    />
                    <span
                      className="inline-block h-2 w-2 rounded-full"
                      style={{ backgroundColor: t.color || "#64748b" }}
                    />
                    <span className="text-sm text-slate-700">{t.name}</span>
                    <span className="ml-auto text-[11px] text-slate-400">
                      {t.members_count} member{t.members_count !== 1 ? "s" : ""}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Individual assignees */}
          {members.length > 0 && (
            <div className="space-y-1.5">
              <Label className="flex items-center gap-1.5">
                <User className="h-3.5 w-3.5 text-slate-400" /> Individual Assignees
              </Label>
              <div className="max-h-40 overflow-y-auto rounded-lg border border-slate-200 divide-y divide-slate-100">
                {members.map((m) => {
                  const userId = m.user?.id
                  const userName = m.user?.name || m.user?.email || "Unknown"
                  const roleName = m.role?.name || m.role?.slug || ""
                  return (
                    <label key={m.id} className="flex cursor-pointer items-center gap-3 px-3 py-2 hover:bg-slate-50">
                      <Checkbox
                        checked={poolUserIds.has(userId)}
                        onCheckedChange={() => toggleMember(userId, roleName)}
                      />
                      <span className="text-sm text-slate-700">{userName}</span>
                      <span className="ml-auto text-[11px] text-slate-400">{roleName}</span>
                    </label>
                  )
                })}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving || !form.name.trim()}>
              {saving && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
              {rule ? "Save Changes" : "Create Rule"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default function DistributionRulesPage() {
  const { toast } = useToast()
  const [rules, setRules] = useState([])
  const [loading, setLoading] = useState(true)
  const [modal, setModal] = useState(false)
  const [editing, setEditing] = useState(null)

  useEffect(() => {
    fetchRules().then(setRules).catch(() => setRules([])).finally(() => setLoading(false))
  }, [])

  async function handleDelete(id) {
    if (!confirm("Delete this rule?")) return
    try {
      await deleteRule(id)
      setRules((r) => r.filter((x) => x.id !== id))
      toast({ title: "Rule deleted" })
    } catch {
      toast({ variant: "destructive", title: "Could not delete rule" })
    }
  }

  async function handleToggle(rule) {
    try {
      const updated = await updateRule(rule.id, { active: !rule.active })
      setRules((prev) => prev.map((r) => (r.id === rule.id ? updated : r)))
    } catch {
      toast({ variant: "destructive", title: "Could not update rule" })
    }
  }

  function handleSaved(saved) {
    setRules((prev) => {
      const exists = prev.find((r) => r.id === saved.id)
      return exists ? prev.map((r) => (r.id === saved.id ? saved : r)) : [saved, ...prev]
    })
  }

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-[17px] font-bold text-slate-900">Lead Distribution</h2>
          <p className="text-[13px] text-slate-500">
            Auto-assign incoming leads with SLA tracking. Rules are evaluated top-to-bottom.
          </p>
        </div>
        <Can resource="settings" action="update">
          <Button size="sm" onClick={() => { setEditing(null); setModal(true) }}>
            <Plus className="mr-1.5 h-3.5 w-3.5" /> New Rule
          </Button>
        </Can>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-slate-300" />
        </div>
      ) : rules.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-200 py-16 text-center">
          <Zap className="mx-auto mb-3 h-8 w-8 text-slate-200" />
          <p className="text-slate-500">No Distribution Rules Yet</p>
          <p className="mt-1 text-sm text-slate-400">Create a rule to auto-assign leads and track SLAs.</p>
          <Can resource="settings" action="update">
            <Button variant="outline" size="sm" className="mt-4" onClick={() => setModal(true)}>
              <Plus className="mr-1.5 h-3.5 w-3.5" /> Create First Rule
            </Button>
          </Can>
        </div>
      ) : (
        <div className="space-y-3">
          {rules.map((rule, i) => (
            <div key={rule.id} className="flex items-start gap-4 rounded-xl border border-slate-200 bg-white p-4">
              <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-slate-100 text-[11px] font-bold text-slate-500">
                {i + 1}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-slate-800">{rule.name}</span>
                  {!rule.active && (
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] text-slate-500">Paused</span>
                  )}
                </div>
                <div className="mt-1 flex flex-wrap gap-3 text-[12px] text-slate-500">
                  <span className="flex items-center gap-1">
                    <ShieldCheck className="h-3 w-3" />
                    SLA: {rule.sla_minutes} min
                  </span>
                  <span>
                    Strategy: {STRATEGIES.find((s) => s.value === rule.strategy)?.label || rule.strategy}
                  </span>
                  {(() => {
                    const pool = rule.assignee_pool || []
                    const userCount = pool.filter((e) => e.user_id).length
                    const teamCount = pool.filter((e) => e.team_id).length
                    const parts = []
                    if (teamCount > 0) parts.push(`${teamCount} team${teamCount !== 1 ? "s" : ""}`)
                    if (userCount > 0) parts.push(`${userCount} member${userCount !== 1 ? "s" : ""}`)
                    return parts.length > 0 ? <span>{parts.join(" · ")}</span> : null
                  })()}
                </div>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                <Can resource="settings" action="update">
                  <Switch
                    checked={rule.active}
                    onCheckedChange={() => handleToggle(rule)}
                  />
                  <Button variant="ghost" size="icon" onClick={() => { setEditing(rule); setModal(true) }} className="h-auto w-auto rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600">
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => handleDelete(rule.id)} className="h-auto w-auto rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-500">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </Can>
              </div>
            </div>
          ))}
        </div>
      )}

      <RuleModal
        open={modal}
        onClose={() => { setModal(false); setEditing(null) }}
        onSaved={handleSaved}
        rule={editing}
      />
    </div>
  )
}
